<template>
    <div id="g2link-details">
        <router-link 
            class="btn btn-small btn-secondary"
            :to="{ name: 'Devices' }"
        >
        Back to device list</router-link>
        <div class="d-flex flex-column flex-sm-row  align-items-center justify-content-between">
            <h2>Configuration for {{ device.name }}</h2>
            <div class="d-flex">
                <div class="mx-1 mb-1">
                    <span v-if="status.connection === '1'" class="badge badge-success">
                        Connected
                    </span>
                    <span v-else-if="status.connection === '2'" class="badge badge-warning">
                        Connecting
                    </span>
                    <span v-else class="badge badge-danger">
                        Not Connected  <span @click="openSocketConnection"><i class="fa fa-refresh" aria-hidden="true"></i></span>
                    </span>
                </div>
                <div class="mx-1 mb-1">
                    <span v-if="status.interlock_registered === '1'" class="badge badge-success">
                        Registered
                    </span>
                    <span v-else class="badge badge-danger">
                        Not Registered
                    </span>
                </div>
                <div class="mx-1 mb-1">
                    <span v-if="status.interlock_call_active === '1'" class="badge badge-success">
                        Call Active
                    </span>
                    <span v-else-if="status.interlock_call_active === '2'" class="badge badge-warning">
                        Dialing Out
                    </span>
                    <span v-else-if="status.interlock_call_active === '3'" class="badge badge-primary">
                        Receiving Call
                    </span>
                    <span v-else class="badge badge-danger">
                        Call Inactive
                    </span>
                </div>
            </div>
        </div>
        <div class="row">
            <div class="col-6 col-md-4 col-lg-2">
                <table class="table table-sm table-bordered">
                    <thead class="thead-light">
                        <tr>
                            <th colspan="2">Volume Inputs</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(value, key) in config.volume_input">
                            <td>
                                <div class="d-flex justify-content-between">
                                    <label :for="key" class="m-0">{{ label(key) }}</label>
                                    <input
                                        :id="key"
                                        type="number"
                                        min="0" max="100" step="5"
                                        v-model="config.volume_input[key]"
                                        @change="e => {
                                            handleVolumeNumberChange(e, config);
                                            handleInputDebounced(e);
                                        }"
                                        :disabled="status.connection !== '1'"
                                    />
                                </div>
                                <input
                                    :id="key"
                                    type="range"
                                    min="0" max="100" step="5"
                                    v-model="config.volume_input[key]"
                                    @change="handleInputDebounced"
                                    :disabled="status.connection !== '1'"
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="col-6 col-md-4 col-lg-2">
                <table class="table table-sm table-bordered">
                    <thead class="thead-light">
                        <tr>
                            <th colspan="2">Volume Outputs</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(value, key) in config.volume_output">
                            <td>
                                <div class="d-flex justify-content-between">
                                    <label :for="key" class="m-0">{{ label(key) }}</label>
                                    <input
                                        :id="key"
                                        type="number"
                                        min="0" max="100" step="5"
                                        v-model="config.volume_output[key]"
                                        @change="e => {
                                            handleVolumeNumberChange(e, config);
                                            handleInputDebounced(e);
                                        }"
                                        :disabled="status.connection !== '1'"
                                    />
                                </div>
                                <input
                                    :id="key"
                                    type="range"
                                    min="0" max="100" step="5"
                                    v-model="config.volume_output[key]"
                                    @change="handleInputDebounced"
                                    :disabled="status.connection !== '1'"
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="col-12 col-md-4">
                <form @submit.prevent="showConfirmModal('sip')" @reset.prevent="getSipConfig">
                    <table class="table table-sm table-bordered">
                        <thead class="thead-light">
                            <tr>
                                <th>SIP</th>
                                <th class="text-right p-0">
                                    <button type="reset" :disabled="status.connection !== '1'" class="btn btn-sm btn-secondary">Reset</button>
                                    <button type="submit" :disabled="status.connection !== '1'" class="btn btn-sm btn-secondary">Save</button>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(value, key) in config.sip">
                                <td>{{ label(key) }}</td>
                                <td>
                                    <select 
                                        v-if="key === 'sip_tts_ext1' || key === 'sip_tts_ext2'"
                                        :id="key"
                                        v-model="config.sip[key]"
                                        :disabled="status.connection !== '1'"
                                    >
                                        <option v-if="key === 'sip_tts_ext1'" value="EXT1">Calling Extension 1</option>
                                        <option v-else value="EXT2">Calling Extension 2</option>
                                        <option value="OFFICE">Calling Office</option>
                                        <option value="EMRGNCY">Calling Emergency</option>
                                        <option value="DISABLED">Disabled</option>
                                    </select>
                                    <select v-else-if="key === 'sip_autoanswer'"
                                        :id="key"
                                        v-model="config.sip[key]"
                                        :disabled="status.connection !== '1'"
                                    >
                                        <option value="1">ON</option>
                                        <option value="0">OFF</option>
                                    </select>
                                    <input v-else
                                        :id="key"
                                        v-model="config.sip[key]"
                                        :disabled="status.connection !== '1'"
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </form>
            </div>
            <div class="col-12 col-md-6 col-lg-4">
                <form @submit.prevent="showConfirmModal('network')" @reset.prevent="getEthernetConfig">
                    <table class="table table-sm table-bordered">
                        <thead class="thead-light">
                            <tr>
                                <th>Network</th>
                                <th class="text-right p-0">
                                    <button type="reset" :disabled="status.connection !== '1'" class="btn btn-sm btn-secondary">Reset</button>
                                    <button type="submit" :disabled="status.connection !== '1'" class="btn btn-sm btn-secondary">Save</button>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(value, key) in config.network">
                                <td>{{ label(key) }}</td>
                                <td>
                                    <select 
                                        v-if="key === 'ethernet_text_to_speech'"
                                        v-model="config.network[key]"
                                        :disabled="status.connection !== '1'"
                                    >
                                        <option value="1">ON</option>
                                        <option value="0">OFF</option>
                                    </select>
                                    <select 
                                        v-else-if="key === 'ethernet_ip_mode'"
                                        v-model="config.network[key]"
                                        :disabled="status.connection !== '1'"
                                    >
                                        <option value="dhcp">DHCP</option>
                                        <option value="static">Static</option>
                                    </select>
                                    <input v-else
                                        :id="key"
                                        v-model="config.network[key]"
                                        :disabled="status.connection !== '1' || key === 'ethernet_mac' || ['ethernet_ip', 'ethernet_gateway', 'ethernet_netmask'].includes(key) && config.network.ethernet_ip_mode === 'dhcp'"
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </form>
                <table class="table table-sm table-bordered">
                    <thead class="thead-light">
                        <tr>
                            <th colspan="2">DNS</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(value, key) in config.dns">
                            <td>{{ label(key) }}</td>
                            <td>
                                <input
                                    :id="key"
                                    v-model="config.dns[key]"
                                    @change="handleInput"
                                    :disabled="status.connection !== '1'"
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="col-12 col-md-6 col-lg-4">
                <table class="table table-sm table-bordered">
                    <thead class="thead-light">
                        <tr>
                            <th colspan="2">Band</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(value, key) in config.band">
                            <td>{{ label(key) }}</td>
                            <td>
                                <select
                                    v-if="key === 'band_preset'"
                                    :id="key"
                                    v-model="config.band[key]"
                                    @change="handleInput"
                                    :disabled="status.connection !== '1'"
                                >
                                    <option value="0">Custom</option>
                                    <option value="1">Flat</option>
                                    <option value="2">Full</option>
                                    <option value="3">Low</option>
                                    <option value="4">Mid</option>
                                    <option value="5">High</option>
                                </select>
                                <select
                                    v-else-if="key === 'band_enable'"
                                    :id="key"
                                    v-model="config.band[key]"
                                    @change="handleInput"
                                    :disabled="status.connection !== '1'"
                                >
                                    <option value="1">ON</option>
                                    <option value="0">OFF</option>
                                </select>
                                <div v-else class="d-flex">
                                    <input 
                                        :id="key"
                                        type="range"
                                        min="-5" max="5" step="1"
                                        v-model="config.band[key]"
                                        @change="handleInputDebounced"
                                        :disabled="status.connection !== '1'"
                                    />
                                    <label class="text-center w-25">
                                        {{ config.band[key] }}
                                    </label>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="col-12 col-md-6 col-lg-4">
                <table class="table table-sm table-bordered">
                    <thead class="thead-light">
                        <tr>
                            <th colspan="2">Keypad</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(value, key) in config.keypad">
                            <td>{{ label(key) }}</td>
                            <td>
                                <select
                                    :id="key"
                                    v-model="config.keypad[key]"
                                    @change="handleInput"
                                    :disabled="status.connection !== '1'"
                                >
                                    <option value="EXT1">Call SIP Extension 1</option>
                                    <option value="EXT2">Call SIP Extension 2</option>
                                    <option value="ANSWER">Answer SIP</option>
                                    <option value="HANGUP">Hangup SIP</option>
                                    <option value="PGUP">Page Up</option>
                                    <option value="PGDWN">Page Down</option>
                                    <option value="DISABLED">Disabled</option>
                                </select>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="col-12 col-md-6 col-lg-4">
                <table class="table table-sm table-bordered">
                    <thead class="thead-light">
                        <tr>
                            <th colspan="2">Bluetooth</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(value, key) in config.bluetooth">
                            <td>{{ label(key) }}</td>
                            <td>
                                <select 
                                    v-if="key === 'bluetooth_use_pairing'"
                                    :id="key"
                                    v-model="config.bluetooth[key]"
                                    @change="handleInput"
                                    :disabled="status.connection !== '1'"
                                >
                                    <option value="true">ON</option>
                                    <option value="false">OFF</option>
                                </select>
                                <input
                                    v-else
                                    :id="key"
                                    v-model="config.bluetooth[key]"
                                    @change="handleInput"
                                    :disabled="status.connection !== '1'"
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
                <table class="table table-sm table-bordered">
                    <thead class="thead-light">
                        <tr>
                            <th colspan="2">General</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(value, key) in config.general">
                            <td>{{ label(key) }}</td>
                            <td>
                                <input
                                    :id="key"
                                    v-model="config.general[key]"
                                    @change="handleInput"
                                    :disabled="status.connection !== '1'"
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
        <Modal modal_id="confirmModal" size="md">
            <template #title>
                Confirm
            </template>
            <template #body>
                <div v-if="modal_type === 'network'">
                    This change will restart the network interface of the device. Do you want to continue?
                </div>
                <div v-else-if="modal_type === 'sip'">
                    This change will restart the sip interface of the device. Do you want to continue?
                </div>
                <div class="d-flex justify-content-end">
                    <button class="btn btn-secondary mr-2" @click.prevent="hideConfirmModal">Cancel</button>
                    <button v-if="modal_type === 'network'" class="btn btn-primary" @click.prevent="setEthernetConfig">Continue</button>
                    <button v-else-if="modal_type === 'sip'" class="btn btn-primary" @click.prevent="setSipConfig">Continue</button>
                </div>
            </template>
        </Modal>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Modal from '../../components/Modal.vue';
import WebSocketConnection from '../../assets/js/socket';
import { initialConfig, configLabelMap } from '../../assets/js/g2link-config';
import debounce from 'lodash/debounce';

export default {
    props: ['device_id'],
    components: { Modal },
    data() {
        return {
            socket: null,
            status: {
                connection: '2',
                interlock_registered: '',
                interlock_call_active: '',
            },
            last_status_received_timestamp: null,
            config: JSON.parse(JSON.stringify(initialConfig)),
            modal_type: 'network',
            auth: '0d7f9280e80fd038658dc8396cdf3ce6'
        }
    },
    computed: {
        ...mapGetters({
            device: 'singleDevice'
        })
    },
    watch: {
        device_id: {
            handler(id) {
                if(this.socket) {
                    this.closeSocketConnection();
                }
                this.$store.dispatch('getDevice', id);
            },
            immediate: true
        },
        device(newDevice) {
            if(newDevice) {
                if(this.socket) {
                    this.closeSocketConnection();
                }
                this.openSocketConnection();
            }
        }
    },
    methods: {
        handleVolumeNumberChange(e, config) {
            if(e.target.value === '' || Number(e.target.value) < 0) {
                e.target.value = 0
                e.target.id.includes('input') ? config.volume_input[e.target.id] = 0 : config.volume_output[e.target.id] = 0;
            } else if (Number(e.target.value) >= 100) {
                e.target.value = 100
                e.target.id.includes('input') ? config.volume_input[e.target.id] = 100 : config.volume_output[e.target.id] = 100;
            } else {
                e.target.value = Number(e.target.value);
                e.target.id.includes('input') ? config.volume_input[e.target.id] = e.target.value : config.volume_output[e.target.id] = e.target.value;
            }
        },
        handleInput(e) {
            this.setSingleConfig(e.target.id.replace(/_/g, '-'), e.target.value);
        },
        handleInputDebounced: debounce(function(e) { 
            this.handleInput(e);
        }, 300),
        showConfirmModal(modalType) {
            this.modal_type = modalType
            $('#confirmModal').modal('show');
        },
        hideConfirmModal() {
            $('#confirmModal').modal('hide');
        },
        getStatus() {
            const elapsedSeconds = this.last_status_received_timestamp ? Math.abs(new Date(this.last_status_received_timestamp) - new Date()) / 1000 : 0;

            if(elapsedSeconds > 5) {
                this.closeSocketConnection();
                return;
            }

            this.sendSocketMessage({ operation: 'sip', action: 'interlock-call-active' });
            this.sendSocketMessage({ operation: 'sip', action: 'interlock-registered' });
        },
        getConfig() {
            for(const group in this.config) {
                for(const name in this.config[group]) {
                    this.getSingleConfig(name);
                }
            }
            this.getEthernetIP();
        },
        getSingleConfig(name) {
            const message = {
                operation: 'read',
                name: name.replace(/_/g, '-')
            }
            this.sendSocketMessage(message);
        },
        setSingleConfig(name, value) {
            const message = {
                operation: 'write',
                name: name,
                value: value
            }
            this.sendSocketMessage(message);
            if(name === 'machine-name' && value !== this.device.name) {
                this.device.name = value;
                this.$store.dispatch('addEditDevice', { device: this.device });
            }
        },
        updateSingleConfig(name, value) {
            this.setSingleConfig(name, value);
            this.getSingleConfig(name);
        },
        syncConfig() {
            this.updateSingleConfig('machine-name', this.device.name);
            this.updateSingleConfig('sip-auth-name', this.device.extension);
            this.updateSingleConfig('sip-auth-pass', this.device.extension);
        },
        getEthernetIP() {
            this.sendSocketMessage({ operation: 'get-current-ip', name: 'ethernet' });
        },
        getEthernetConfig() {
            for(const name in this.config.network) {
                this.getSingleConfig(name);
            }
            this.getEthernetIP();
        },
        setEthernetConfig() {
            this.hideConfirmModal();

            const message = {
                operation: 'postForm',
                'form-complete-operation': 'ethernet-reset-interface',
                'form-name': 'NetworkForm-Ethernet',
                data: []
            }
            for(const key in this.config.network) {
                message.data.push(key.replace(/_/g, '-'));
                message.data.push(this.config.network[key]);
            }
            this.sendSocketMessage(message);

            if(this.config.network.ethernet_ip !== this.device.ip) {
                this.device.ip = this.config.network.ethernet_ip;
                this.$store.dispatch('addEditDevice', {
                    device: this.device
                }).then(() => {
                    this.$store.dispatch('getDevice', this.device_id);
                })
            }
        },
        getSipConfig() {
            for(const name in this.config.sip) {
                this.getSingleConfig(name);
            }
        },
        setSipConfig() {
            this.hideConfirmModal();

            const message = {
                operation: 'postForm',
                'form-complete-operation': 'sip-reset-interface',
                'form-name': 'SIP-form',
                data: []
            }
            for(const key in this.config.sip) {
                message.data.push(key.replace(/_/g, '-'));
                message.data.push(this.config.sip[key]);
            }
            this.sendSocketMessage(message)
        },
        openSocketConnection(options = { maxReconnections: 5 }) {
            if(!this.socket) {
                this.status.connection = '2';
                this.socket = new WebSocketConnection(`ws://${this.device.ip}/websocket`, 'interlock-protocol', options);
                this.socket.on('message', this.receiveSocketMessage);
                this.socket.on('open', () => {
                    this.status.connection = '1';
                    this.getConfig();
                    this.getStatus();
                    this.syncConfig();
                    this.statusInterval = setInterval(this.getStatus, 2000);
                });
                this.socket.on('close', (e) => {
                    if(e.target.url === `ws://${this.device.ip}/websocket`)
                        this.closeSocketConnection();
                });
                this.socket.connect();
            }
        },
        closeSocketConnection() {
            this.status.connection = '0';
            clearInterval(this.statusInterval);
            this.last_status_received_timestamp = null;
            this.statusInterval = null;
            if(this.socket) {
                this.socket.disconnect();
                this.socket = null;
            }
        },
        sendSocketMessage(message) {
            if(this.socket) {
                this.socket.send({ ...message, authentication_token: this.auth });
            }
        },
        receiveSocketMessage(message) {
            const operation = message.operation;
            if(operation === 'read') {
                const name = message.name.replace(/-/g, '_');
                const value = message.value;
                let group = '';

                if(name.includes('volume_input'))
                    group = 'volume_input';
                else if(name.includes('volume_output'))
                    group = 'volume_output';
                else if(name.includes('bluetooth'))
                    group = 'bluetooth';
                else if(name.includes('ethernet'))
                    group = 'network';
                else if(name.includes('dns'))
                    group = 'dns';
                else if(name.includes('sip'))
                    group = 'sip';
                else if(name.includes('band'))
                    group = 'band';
                else if(name.includes('keypad'))
                    group = 'keypad';
                else group = 'general';

                this.config[group][name] = value;
            } else if(operation === 'sip') {
                const action = message.action.replace(/-/g, '_');
                const status = message.status;
                this.status[action] = status;
                this.last_status_received_timestamp = Date.now();
            } else if(operation === 'get-current-ip') {
                const name = message.name;
                const value = message.value;
                if(name === 'ethernet') {
                    this.config.network.ethernet_ip = value;
                }
            }
        },
        label(key) {
            return configLabelMap.get(key);
        }
    },
    beforeDestroy() {
        this.closeSocketConnection();
    }
}
</script>

<style scoped>
input, select {
    width: 100%;
}

/* Hide number box controls for Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type=number] {
    /* Hide number box controls for Firefox */
  -moz-appearance: textfield;
  appearance: textfield;
  border: none;

  width: 2rem;
  height: 1.25rem;
}
</style>